import { Component, computed, effect, inject, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { TicketsFacade, TicketsExecutionStore } from '@state';
import { TicketsAutomationService } from '@services';

@Component({
  selector: 'app-execution-fab',
  standalone: true,
  imports: [DecimalPipe],
  host: {
    '[class.define-offset]': 'isDefineRoute()',
  },
  styles: [
    `
      :host {
        position: fixed;
        right: 1rem;
        bottom: 1rem;
        z-index: 1060;
      }
      :host(.define-offset) {
        bottom: 4.25rem;
      }
      .fab-button {
        border-radius: 999px;
        box-shadow: 0 0.35rem 1rem rgba(0, 0, 0, 0.24);
      }
      .fab-panel {
        width: min(92vw, 360px);
        max-height: min(calc(100vh - 6rem), 42rem);
        overflow: auto;
        border-radius: 0.75rem;
        border: 1px solid color-mix(in srgb, var(--color-border) 75%, transparent);
        background: var(--color-surface);
        color: var(--text-primary);
        box-shadow: 0 0.35rem 1rem rgba(0, 0, 0, 0.24);
      }
      .fab-panel .text-muted {
        color: var(--text-muted) !important;
      }
      .fab-panel a {
        color: var(--color-primary);
      }
      .phase {
        text-transform: capitalize;
      }
      .run-items {
        max-height: min(45vh, 22rem);
        overflow: auto;
      }
      .run-item {
        border: 1px solid color-mix(in srgb, var(--color-border) 75%, transparent);
        border-radius: 0.5rem;
      }
      .decision-box {
        border: 1px solid color-mix(in srgb, var(--color-warning, #d39e00) 45%, var(--color-border));
        border-radius: 0.5rem;
        background: color-mix(in srgb, var(--color-surface) 88%, #fff3cd 12%);
      }
      .inspector-panel {
        position: fixed;
        right: 1rem;
        top: 1rem;
        bottom: 4.5rem;
        width: min(92vw, 860px);
        height: min(calc(100vh - 5.5rem), 860px);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border-radius: 0.85rem;
        border: 1px solid color-mix(in srgb, var(--color-border) 75%, transparent);
        background: var(--color-surface);
        color: var(--text-primary);
        box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.28);
        z-index: 1061;
      }
      .inspector-body {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        padding-right: 0.25rem;
      }
      .inspector-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
      }
      .inspector-card {
        border: 1px solid color-mix(in srgb, var(--color-border) 75%, transparent);
        border-radius: 0.6rem;
        padding: 0.75rem;
      }
      .timeline {
        max-height: 22rem;
        overflow: auto;
      }
      .timeline-entry {
        border-left: 2px solid color-mix(in srgb, var(--color-primary) 55%, transparent);
        padding-left: 0.75rem;
      }
      @media (max-width: 900px) {
        .inspector-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
  template: `
    @if (showFab()) {
      <div class="d-flex flex-column align-items-end gap-2">
        @if (inspectorOpen()) {
          <div class="inspector-panel p-3">
            <div class="d-flex align-items-start justify-content-between gap-2">
              <div>
                <div class="fw-semibold">Run Inspector</div>
                <div class="small text-muted">
                  {{ execution().phase }} ·
                  @if (execution().liveFollowConnected) {
                    live follow connected
                  } @else {
                    polling fallback
                  }
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleInspector()">Close</button>
            </div>
            <hr class="my-3" />
            <div class="inspector-body">
              <div class="inspector-grid">
                <div class="inspector-card">
                  <div class="fw-semibold mb-2">Overview</div>
                  <div><span class="fw-semibold">Run:</span> {{ execution().runId || '—' }}</div>
                  <div><span class="fw-semibold">Ticket:</span> {{ execution().ticketTitle || execution().ticketId || '—' }}</div>
                  <div><span class="fw-semibold">Repo:</span> {{ execution().repoFullName || '—' }}</div>
                  <div><span class="fw-semibold">Model:</span> {{ execution().model || 'default' }}</div>
                  @if (execution().reviewAutomation) {
                    <div class="mt-2">
                      <span class="fw-semibold">Review automation:</span>
                      {{ execution().reviewAutomation?.status }}
                      @if (execution().reviewAutomation?.message) {
                        <span class="text-muted">- {{ execution().reviewAutomation?.message }}</span>
                      }
                    </div>
                  }
                  @if (execution().plannerRepoConfidence?.length) {
                    <div class="mt-2 pt-2">
                      <div class="small text-muted mb-1">Skill confidence (planning time)</div>
                      @for (row of execution().plannerRepoConfidence!; track row.owner + row.repo) {
                        <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <span class="text-muted small">{{ row.owner }}/{{ row.repo }}</span>
                          <span class="badge" [class.bg-success]="row.verdict === 'high'"
                                [class.bg-primary]="row.verdict === 'med'"
                                [class.bg-danger]="row.verdict === 'low'"
                                [class.bg-secondary]="row.verdict === 'unknown'">
                            {{ row.verdict }}
                          </span>
                          <span class="text-muted small">{{ row.scorePct }}%</span>
                          @if (row.skillId) {
                            <span class="badge bg-light text-dark small">{{ row.skillId }}</span>
                          }
                        </div>
                        <div class="small text-muted text-break mb-2">{{ row.reason }}</div>
                      }
                    </div>
                  }
                  @if (execution().mergeReadiness) {
                    <div class="mt-2">
                      <div><span class="fw-semibold">Merge readiness:</span> {{ execution().mergeReadiness?.state }}</div>
                      @if (execution().mergeReadiness?.score !== undefined) {
                        <div class="text-muted small">Score: {{ execution().mergeReadiness?.score }}</div>
                      }
                      <div class="small text-muted">
                        Bugbot: {{ execution().mergeReadiness?.bugbotApprovalState || 'unknown' }} ·
                        Checks: {{ execution().mergeReadiness?.requiredChecksState || 'unknown' }}
                      </div>
                      @if (execution().mergeReadiness?.blockerReason) {
                        <div class="small text-break mt-1">{{ execution().mergeReadiness?.blockerReason }}</div>
                      }
                      @if (execution().mergeReadiness?.bugbotIdentity?.reviewerLogin || execution().mergeReadiness?.bugbotIdentity?.appName) {
                        <div class="small text-muted mt-1">
                          Bugbot identity:
                          {{ execution().mergeReadiness?.bugbotIdentity?.reviewerLogin || execution().mergeReadiness?.bugbotIdentity?.appName }}
                        </div>
                      }
                      @if (execution().mergeReadiness?.confidence; as confidence) {
                        <div class="mt-2 pt-2 confidence-block">
                          <div class="d-flex align-items-center gap-2 flex-wrap">
                            <span class="fw-semibold">Skill confidence (merge / outcomes):</span>
                            <span class="badge" [class.bg-success]="confidence.verdict === 'high'"
                                  [class.bg-primary]="confidence.verdict === 'med'"
                                  [class.bg-danger]="confidence.verdict === 'low'"
                                  [class.bg-secondary]="confidence.verdict === 'unknown'">
                              {{ confidence.verdict || 'unknown' }}
                            </span>
                            <span class="text-muted small">
                              {{ (confidence.score * 100) | number:'1.0-0' }}%
                            </span>
                            @if (confidence.skillId) {
                              <span class="badge bg-light text-dark small">{{ confidence.skillId }}</span>
                            }
                          </div>
                          <div class="small text-muted mt-1">{{ confidence.reason }}</div>
                          @if (confidence.signals) {
                            <div class="small text-muted mt-1">
                              +{{ confidence.signals.positives }} ·
                              -{{ confidence.signals.negatives }} ·
                              ~{{ confidence.signals.neutrals }}
                              @if (confidence.signals.distilledRatio !== undefined) {
                                · {{ (confidence.signals.distilledRatio * 100) | number:'1.0-0' }}% distilled
                              }
                              @if (confidence.signals.firstPassShare !== undefined && confidence.signals.positives > 0) {
                                · {{ (confidence.signals.firstPassShare * 100) | number:'1.0-0' }}% first-pass
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                  @if (execution().pullRequestUrl) {
                    <div class="mt-2">
                      <a [href]="execution().pullRequestUrl" target="_blank" rel="noopener noreferrer">Open primary PR</a>
                    </div>
                  }
                </div>

                <div class="inspector-card">
                  <div class="fw-semibold mb-2">Timeline</div>
                  <div class="timeline d-flex flex-column gap-3">
                    @for (event of timelineEvents(); track event.seq) {
                      <div class="timeline-entry">
                        <div class="d-flex justify-content-between gap-2">
                          <div class="fw-semibold">{{ event.title }}</div>
                          <div class="text-muted">{{ event.type }}</div>
                        </div>
                        @if (event.phase) {
                          <div class="text-muted small">{{ event.phase }}</div>
                        }
                        @if (event.owner || event.repo) {
                          <div class="text-muted small">{{ event.owner }}/{{ event.repo }}</div>
                        }
                        @if (event.command) {
                          <div class="small text-break mt-1">{{ event.command }}</div>
                        }
                        @if (event.message) {
                          <div class="small mt-1 text-break">{{ event.message }}</div>
                        }
                      </div>
                    }
                  </div>
                </div>

                <div class="inspector-card">
                  <div class="fw-semibold mb-2">Work Units</div>
                  <div class="run-items d-flex flex-column gap-2">
                    @for (unit of detailedWorkUnits(); track unit.id) {
                      <div class="run-item p-2">
                        <div class="d-flex justify-content-between gap-2">
                          <div class="fw-semibold">{{ unit.owner }}/{{ unit.repo }}</div>
                          <div class="text-muted">{{ unit.status }}</div>
                        </div>
                        <div class="d-flex align-items-center gap-1 mt-1 flex-wrap">
                          @if (unit.skillId) {
                            <span class="badge bg-light text-dark small" title="Skill the router chose for this work unit">
                              {{ unit.skillId }}
                            </span>
                          }
                          @if (unit.role) {
                            <span class="badge bg-light text-dark small">role: {{ unit.role }}</span>
                          }
                        </div>
                        <div class="text-muted mt-1">{{ unit.goal }}</div>
                        @if (unit.pullRequest?.url) {
                          <div class="mt-1">
                            <a [href]="unit.pullRequest?.url" target="_blank" rel="noopener noreferrer">
                              {{ unit.pullRequest?.title || 'Open PR' }}
                            </a>
                          </div>
                        }
                        @if (unit.commands?.length) {
                          <div class="small mt-2">
                            Last command: {{ unit.commands?.[unit.commands?.length! - 1]?.command }}
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>

                <div class="inspector-card">
                  <div class="fw-semibold mb-2">Artifacts And Commands</div>
                  @if (execution().latestCommands?.length) {
                    <div class="fw-semibold small mb-1">Recent commands</div>
                    <div class="run-items d-flex flex-column gap-2 mb-3">
                      @for (command of execution().latestCommands || []; track command.id) {
                        <div class="run-item p-2">
                          <div class="d-flex justify-content-between gap-2">
                            <div class="fw-semibold">{{ command.phase }}</div>
                            <div class="text-muted">{{ command.status }}</div>
                          </div>
                          <div class="small text-break">{{ command.command }}</div>
                          @if (command.stderr) {
                            <div class="small text-danger mt-1 text-break">{{ command.stderr }}</div>
                          } @else if (command.stdout) {
                            <div class="small text-muted mt-1 text-break">{{ command.stdout }}</div>
                          }
                        </div>
                      }
                    </div>
                  }
                  @if (execution().latestArtifacts?.length) {
                    <div class="fw-semibold small mb-1">Artifacts</div>
                    <div class="run-items d-flex flex-column gap-2">
                      @for (artifact of execution().latestArtifacts || []; track artifact.label + artifact.path) {
                        <div class="run-item p-2">
                          <div class="d-flex justify-content-between gap-2">
                            <div class="fw-semibold">{{ artifact.label }}</div>
                            <div class="text-muted">{{ artifact.type }}</div>
                          </div>
                          @if (artifact.path) {
                            <div class="small text-muted">{{ artifact.path }}</div>
                          }
                          @if (artifact.content) {
                            <div class="small mt-1 text-break">{{ artifact.content }}</div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }

        @if (expanded()) {
          <div class="fab-panel p-3">
            <div class="d-flex align-items-start justify-content-between gap-2">
              <div>
                <div class="fw-semibold">{{ canStartBuild() && execution().phase === 'idle' ? 'Project build' : execution().mode === 'build' ? 'Project build' : 'Agent execution' }}</div>
                <div class="small text-muted phase">{{ canStartBuild() && execution().phase === 'idle' ? 'ready' : execution().phase }}</div>
              </div>
              <div class="d-flex gap-1">
                @if (execution().active) {
                  <button type="button" class="btn btn-sm btn-outline-danger" (click)="cancelActiveRun()">Stop</button>
                }
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goToTickets()">Tickets</button>
                <button type="button" class="btn btn-sm btn-light" (click)="toggleExpanded()">Hide</button>
              </div>
            </div>
            <hr class="my-2" />
            <div class="small">
              @if (canStartBuild()) {
                <div class="text-muted mb-2">Start sequential execution for backlog tickets using the current project context.</div>
                <button type="button" class="btn btn-sm btn-primary mb-2" (click)="startBuildProject()" [disabled]="automation.startBusy()">
                  @if (automation.startBusy()) {
                    <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    Starting…
                  } @else {
                    Start Build Project
                  }
                </button>
              }
              @if (execution().mode === 'build' && execution().progress) {
                <div>
                  <span class="fw-semibold">Progress:</span>
                  {{
                    (execution().progress?.completed || 0) +
                      (execution().progress?.failed || 0) +
                      (execution().progress?.manualRequired || 0) +
                      (execution().progress?.skipped || 0)
                  }}/{{ execution().progress?.total || 0 }}
                </div>
              }
              <div><span class="fw-semibold">Ticket:</span> {{ execution().ticketTitle || execution().ticketId || '—' }}</div>
              <div><span class="fw-semibold">Repo:</span> {{ execution().repoFullName || '—' }}</div>
              <div><span class="fw-semibold">Model:</span> {{ execution().model || 'default' }}</div>
              @if (execution().reviewAutomation) {
                <div>
                  <span class="fw-semibold">Review automation:</span>
                  {{ execution().reviewAutomation?.status }}
                  @if (execution().reviewAutomation?.message) {
                    <span class="text-muted">- {{ execution().reviewAutomation?.message }}</span>
                  }
                </div>
              }
              @if (execution().plannerRepoConfidence?.length) {
                <div class="mt-1">
                  <div class="text-muted small">Skill confidence (planning time)</div>
                  @for (row of execution().plannerRepoConfidence!; track row.owner + row.repo) {
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <span class="text-muted small">{{ row.owner }}/{{ row.repo }}</span>
                      <span class="badge small"
                            [class.bg-success]="row.verdict === 'high'"
                            [class.bg-primary]="row.verdict === 'med'"
                            [class.bg-danger]="row.verdict === 'low'"
                            [class.bg-secondary]="row.verdict === 'unknown'"
                            [title]="row.reason">
                        {{ row.verdict }} · {{ row.scorePct }}%
                      </span>
                      @if (row.skillId) {
                        <span class="badge bg-light text-dark small">{{ row.skillId }}</span>
                      }
                    </div>
                  }
                </div>
              }
              @if (execution().mergeReadiness) {
                <div class="mt-1">
                  <span class="fw-semibold">Merge readiness:</span>
                  {{ execution().mergeReadiness?.state }}
                  @if (execution().mergeReadiness?.blockerReason) {
                    <div class="small text-muted mt-1">{{ execution().mergeReadiness?.blockerReason }}</div>
                  }
                  @if (execution().mergeReadiness?.confidence; as confidence) {
                    <div class="d-flex flex-column gap-1 mt-1">
                      <div class="text-muted small">Skill confidence (merge / outcomes)</div>
                      <div class="d-flex align-items-center gap-1 flex-wrap">
                        <span class="badge small"
                              [class.bg-success]="confidence.verdict === 'high'"
                              [class.bg-primary]="confidence.verdict === 'med'"
                              [class.bg-danger]="confidence.verdict === 'low'"
                              [class.bg-secondary]="confidence.verdict === 'unknown'"
                              [title]="confidence.reason">
                          {{ confidence.verdict }} · {{ (confidence.score * 100) | number:'1.0-0' }}%
                        </span>
                        @if (confidence.skillId) {
                          <span class="badge bg-light text-dark small">{{ confidence.skillId }}</span>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
              @if (pendingDecision()) {
                <div class="decision-box p-2 mt-2">
                  <div class="fw-semibold mb-1">Confirmation needed</div>
                  <div>{{ pendingDecision()?.prompt }}</div>
                  @if (pendingDecision()?.proposedFileCount) {
                    <div class="text-muted mt-1">Proposed files: {{ pendingDecision()?.proposedFileCount }}</div>
                  }
                  <div class="d-flex gap-1 mt-2">
                    @if (pendingDecision()?.allowContinue !== false) {
                      <button type="button" class="btn btn-sm btn-primary" (click)="resolvePendingDecision('continue')" [disabled]="decisionBusy()">
                        Continue
                      </button>
                    }
                    @if (pendingDecision()?.allowSkip !== false) {
                      <button type="button" class="btn btn-sm btn-outline-secondary" (click)="resolvePendingDecision('skip')" [disabled]="decisionBusy()">
                        Skip ticket
                      </button>
                    }
                    @if (pendingDecision()?.allowCancel !== false) {
                      <button type="button" class="btn btn-sm btn-outline-danger" (click)="resolvePendingDecision('cancel')" [disabled]="decisionBusy()">
                        Cancel run
                      </button>
                    }
                  </div>
                </div>
              }
              @if (execution().pullRequestUrl) {
                <div>
                  <a [href]="execution().pullRequestUrl" target="_blank" rel="noopener noreferrer">Open PR</a>
                </div>
              }
              @if (run()) {
                <div class="mt-2">
                  <button type="button" class="btn btn-sm btn-outline-primary" (click)="toggleInspector()">Inspect run</button>
                </div>
              }
              @if (execution().error) {
                <div class="text-danger mt-2">{{ execution().error }}</div>
              }
              @if (canResumeFailedRun()) {
                <div class="mt-2">
                  <button type="button" class="btn btn-sm btn-primary" (click)="resumeFailedRun()" [disabled]="decisionBusy()">
                    Resume failed run
                  </button>
                </div>
              }
              @if (execution().workUnits?.length) {
                <div class="mt-3">
                  <div class="fw-semibold mb-2">Repo work units</div>
                  <div class="run-items d-flex flex-column gap-2">
                    @for (unit of execution().workUnits || []; track unit.id) {
                      <div class="run-item p-2">
                        <div class="d-flex justify-content-between gap-2">
                          <div class="fw-semibold">{{ unit.owner }}/{{ unit.repo }}</div>
                          <div class="text-muted">{{ unit.status }}</div>
                        </div>
                        <div class="text-muted mt-1">{{ unit.goal }}</div>
                        @if (unit.pullRequest?.url) {
                          <div class="mt-1">
                            <a [href]="unit.pullRequest?.url" target="_blank" rel="noopener noreferrer">
                              {{ unit.pullRequest?.title || 'Open PR' }}
                            </a>
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
              @if (execution().latestCommands?.length) {
                <div class="mt-3">
                  <div class="fw-semibold mb-2">Recent commands</div>
                  <div class="run-items d-flex flex-column gap-2">
                    @for (command of execution().latestCommands || []; track command.id) {
                      <div class="run-item p-2">
                        <div class="d-flex justify-content-between gap-2">
                          <div class="fw-semibold">{{ command.phase }}</div>
                          <div class="text-muted">{{ command.status }}</div>
                        </div>
                        <div class="small text-break">{{ command.command }}</div>
                        @if (command.exitCode !== undefined) {
                          <div class="text-muted mt-1">Exit code: {{ command.exitCode }}</div>
                        }
                        @if (command.stderr) {
                          <div class="text-danger mt-1 small">{{ command.stderr }}</div>
                        } @else if (command.stdout) {
                          <div class="text-muted mt-1 small">{{ command.stdout }}</div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
              @if (execution().latestArtifacts?.length) {
                <div class="mt-3">
                  <div class="fw-semibold mb-2">Artifacts</div>
                  <div class="run-items d-flex flex-column gap-2">
                    @for (artifact of execution().latestArtifacts || []; track artifact.label + artifact.path) {
                      <div class="run-item p-2">
                        <div class="d-flex justify-content-between gap-2">
                          <div class="fw-semibold">{{ artifact.label }}</div>
                          <div class="text-muted">{{ artifact.type }}</div>
                        </div>
                        @if (artifact.path) {
                          <div class="small text-muted">{{ artifact.path }}</div>
                        }
                        @if (artifact.content) {
                          <div class="small text-break mt-1">{{ artifact.content }}</div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
              @if (visibleItems().length) {
                <div class="mt-3">
                  <div class="fw-semibold mb-2">Run items</div>
                  <div class="run-items d-flex flex-column gap-2">
                    @for (item of visibleItems(); track item.ticketId) {
                      <div class="run-item p-2">
                        <div class="d-flex justify-content-between gap-2">
                          <div class="fw-semibold">{{ item.title }}</div>
                          <div class="text-muted">{{ itemStatusLabel(item.status) }}</div>
                        </div>
                        @if (item.skillId || item.repoRole) {
                          <div class="d-flex align-items-center gap-1 mt-1 flex-wrap">
                            @if (item.skillId) {
                              <span class="badge bg-light text-dark small" title="Skill the router chose">{{ item.skillId }}</span>
                            }
                            @if (item.repoRole) {
                              <span class="badge bg-light text-dark small">role: {{ item.repoRole }}</span>
                            }
                          </div>
                        }
                        @if (item.warning) {
                          <div class="text-warning mt-1">{{ item.warning }}</div>
                        }
                        @if (item.error) {
                          <div class="text-danger mt-1">{{ item.error }}</div>
                        } @else if (item.reason) {
                          <div class="text-muted mt-1">{{ item.reason }}</div>
                        }
                        @if (item.pullRequestUrl) {
                          <div class="mt-1">
                            <a [href]="item.pullRequestUrl" target="_blank" rel="noopener noreferrer">
                              {{ item.pullRequestTitle || 'Open PR' }}
                            </a>
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <button type="button" class="btn btn-primary btn-sm fab-button d-flex align-items-center gap-2" (click)="onFabClick()">
          @if (execution().active) {
            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          } @else if (canStartBuild() && execution().phase === 'idle') {
            <i class="bi bi-play-fill"></i>
          } @else if (execution().phase === 'completed') {
            <i class="bi bi-check-circle"></i>
          } @else if (execution().phase === 'awaiting_input') {
            <i class="bi bi-question-circle"></i>
          } @else if (execution().phase === 'cancelled') {
            <i class="bi bi-stop-circle"></i>
          } @else if (execution().phase === 'partial') {
            <i class="bi bi-hourglass-split"></i>
          } @else if (execution().phase === 'failed') {
            <i class="bi bi-exclamation-triangle"></i>
          }
          <span>{{
            canStartBuild() && execution().phase === 'idle'
              ? 'Execute Ticket Board'
              : execution().active
              ? execution().mode === 'build'
                ? 'Build running'
                : 'Agent running'
              : execution().phase === 'completed'
              ? 'Last run complete'
              : execution().phase === 'awaiting_input'
              ? 'Waiting for input'
              : execution().phase === 'cancelled'
              ? 'Run cancelled'
              : execution().phase === 'partial'
              ? 'Build needs review'
              : 'Execution status'
          }}</span>
        </button>
      </div>
    }
  `,
})
export class ExecutionFabComponent {
  private ticketsStore = inject(TicketsFacade);
  private execStore = inject(TicketsExecutionStore);
  protected automation = inject(TicketsAutomationService);
  private router = inject(Router);
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected expanded = signal(false);
  protected inspectorOpen = signal(false);
  protected decisionBusy = signal(false);
  protected execution = computed(() => this.execStore.execution());
  protected run = computed(() => this.automation.currentRun());
  protected currentDetailedItem = computed(() =>
    (this.run()?.items ?? []).find((item) => String(item?.ticketId ?? '') === String(this.execution().ticketId ?? '')) ??
    (this.run()?.items ?? [])[0] ??
    null
  );
  protected detailedWorkUnits = computed(() => this.currentDetailedItem()?.workUnits ?? []);
  protected timelineEvents = computed(() => (this.execution().events ?? []).slice().reverse().slice(0, 50));
  protected pendingDecision = computed(() => this.run()?.pendingDecision ?? null);
  protected visibleItems = computed(() =>
    (this.run()?.items ?? [])
      .filter((item) => item.status !== 'queued' || item.pullRequestUrl || item.error || item.warning || item.awaitingUserAction)
      .slice()
      .reverse()
      .slice(0, 6)
  );
  protected isDefineRoute = computed(() => this.currentUrl().startsWith('/define'));
  protected isTicketsRoute = computed(() => this.currentUrl().startsWith('/tickets'));
  protected canStartBuild = computed(() => {
    const execution = this.execution();
    return (
      this.isTicketsRoute() &&
      !!this.ticketsStore.activeProjectId() &&
      !execution.active &&
      execution.phase !== 'awaiting_input' &&
      !this.automation.startBusy()
    );
  });
  protected showFab = computed(() => {
    const e = this.execution();
    return this.canStartBuild() || !!e.active || e.phase === 'completed' || e.phase === 'failed' || e.phase === 'partial' || e.phase === 'awaiting_input' || e.phase === 'cancelled';
  });

  constructor() {
    effect(() => {
      const execution = this.execution();
      if (!execution.runId) return;
      const current = this.automation.currentRun();
      if (current && String(current.id ?? '') === String(execution.runId ?? '')) return;
      if (execution.active) {
        this.automation.startPolling(execution.runId, execution.model);
        return;
      }
      void this.automation.hydratePersistedRun(execution.model);
    });
  }

  protected toggleExpanded() {
    this.expanded.update((v) => !v);
  }

  protected toggleInspector() {
    this.inspectorOpen.update((v) => !v);
  }

  protected onFabClick() {
    if (this.canStartBuild() && this.execution().phase === 'idle') {
      void this.startBuildProject();
      return;
    }
    this.toggleExpanded();
  }

  protected goToTickets() {
    void this.router.navigate(['/tickets']);
  }

  protected async startBuildProject() {
    const model = String(this.ticketsStore.executionModelId() ?? '').trim();
    const started = await this.automation.startBuildProject(model);
    if (started) this.expanded.set(true);
  }

  protected itemStatusLabel(status: string | undefined): string {
    const normalized = String(status ?? '').trim().toLowerCase();
    if (normalized === 'awaiting_confirmation') return 'Waiting for input';
    return normalized ? normalized.replace(/_/g, ' ') : 'unknown';
  }

  protected canResumeFailedRun = computed(() => {
    const execution = this.execution();
    if (execution.active) return false;
    if (execution.phase !== 'failed' && execution.phase !== 'partial') return false;
    const item = this.currentDetailedItem();
    return !!item?.resumeDraft?.workUnits?.length;
  });

  protected async cancelActiveRun() {
    const runId = String(this.execution().runId ?? '').trim();
    if (!runId) return;
    this.decisionBusy.set(true);
    try {
      await this.automation.cancelRun(runId, this.execution().model);
    } finally {
      this.decisionBusy.set(false);
    }
  }

  protected async resolvePendingDecision(action: 'continue' | 'skip' | 'cancel') {
    const runId = String(this.execution().runId ?? '').trim();
    if (!runId) return;
    this.decisionBusy.set(true);
    try {
      await this.automation.resolveRunDecision(runId, action, this.execution().model);
    } finally {
      this.decisionBusy.set(false);
    }
  }

  protected async resumeFailedRun() {
    const runId = String(this.execution().runId ?? '').trim();
    if (!runId) return;
    this.decisionBusy.set(true);
    try {
      await this.automation.resumeRun(runId, this.execution().model);
    } finally {
      this.decisionBusy.set(false);
    }
  }
}

